/**
 * Cross-domain navigation helper.
 *
 * Why this exists: GA4's auto-linker decorates outbound links with the
 * `_gl` param so the client ID + session + original UTM attribution
 * carry across to a sibling domain (e.g. this booking subdomain back
 * to the main WordPress thank-you page). The auto-linker hooks fire
 * on real anchor click and form submit events — NOT on
 * `window.location.href = ...` assignments.
 *
 * To ensure the thank-you page lands with an intact GA4 session, we
 * trigger a programmatic click on an invisible anchor. The gtag.js
 * runtime intercepts that click and appends `_gl` to the href before
 * navigation (only when the destination matches a configured linker
 * domain — see /public/index.html).
 *
 * On visitors where GA isn't installed (e.g. clinic site, or the
 * script failed to load), this falls back to a plain `location.href`
 * assignment — behaviour unchanged.
 *
 * We do NOT inject utm_* or gclid/fbclid on the destination — those
 * come from the main site's original referrer and the receiving
 * thank-you page's GA4 install already has them.
 */
export function navigateExternal(url) {
  if (!url || typeof window === "undefined") return;
  try {
    // Only use the anchor-click trick when gtag is present — the whole
    // point is to let GA4's linker decorate the href. Otherwise plain
    // navigation is fine.
    if (typeof window.gtag === "function") {
      const a = document.createElement("a");
      a.href = url;
      a.style.display = "none";
      // Explicitly do NOT set target="_blank" — we want a full-tab
      // navigation so the thank-you page becomes the top-level doc.
      document.body.appendChild(a);
      a.click();
      // Clean up after the browser has started navigation.
      setTimeout(() => { try { a.remove(); } catch (_e) {} }, 0);
      return;
    }
  } catch (_e) {
    // Fall through to the plain assignment on any failure — the
    // booking app must never get stuck because an analytics hook broke.
  }
  window.location.href = url;
}
